import { Fragment, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { RetryableError } from '../../../shared/RetryableError';
import { questionFieldLabel, QUESTION_MAX_LENGTH } from '../../employee-onboarding/questions/questionFields';
import { QuestionFieldEditor } from '../components/QuestionFieldEditor';
import { peopleGateway } from '../index';
import type { EmployeeQuestionItem } from '../types';
import '../employee-list.css';

type Filter = 'open' | 'answered';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const dateText = (iso: string) => new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ReplyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M6.5 3 2.5 7l4 4M2.5 7h7a4 4 0 0 1 4 4v1.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * EPN-216: the questions employees asked about their read-only fields (the "Ask about this" button in the invitation setup), under
 * People > Employees > Employee Questions. Laid out exactly like All Employees: status tabs with counts, title and search, filter pills,
 * rows-per-page and paging bar, then the table with icon actions. The reply is emailed to the employee and shown in their setup; the
 * questioned field can be corrected right in the reply row (QuestionFieldEditor). A refused name lookup just shows "An employee".
 */
export function EmployeeQuestionsPage() {
  const [filter, setFilter] = useState<Filter>('open');
  const [all, setAll] = useState<EmployeeQuestionItem[] | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]!);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [replyingId, setReplyingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setAll(null);
    setReplyingId(null);
    try {
      const list = await peopleGateway.listEmployeeQuestions('all');
      setAll(list);
      const ids = Array.from(new Set(list.map((q) => q.employeeId)));
      const found = await Promise.allSettled(ids.map((id) => peopleGateway.getEmployee(id)));
      const next: Record<string, string> = {};
      found.forEach((r, i) => { if (r.status === 'fulfilled') next[ids[i]!] = `${r.value.firstName} ${r.value.lastName}`.trim(); });
      setNames((prev) => ({ ...prev, ...next }));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load employee questions.'));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- load() resets state, then awaits the gateway
    void load();
  }, [load]);

  const nameOf = (q: EmployeeQuestionItem) => names[q.employeeId] ?? 'An employee';
  const openCount = all?.filter((q) => q.status === 'open').length ?? null;
  const answeredCount = all?.filter((q) => q.status === 'answered').length ?? null;

  const employeeOptions = useMemo(
    () => Array.from(new Set((all ?? []).map((q) => q.employeeId))).map((id) => ({ value: id, label: names[id] ?? 'An employee' })),
    [all, names],
  );
  const fieldOptions = useMemo(
    () => Array.from(new Set((all ?? []).map((q) => q.fieldKey))).map((k) => ({ value: k, label: questionFieldLabel(k) })),
    [all],
  );

  const matching = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (all ?? []).filter((q) => q.status === filter
      && (!employeeFilter || q.employeeId === employeeFilter)
      && (!fieldFilter || q.fieldKey === fieldFilter)
      && (!needle || `${names[q.employeeId] ?? ''} ${questionFieldLabel(q.fieldKey)} ${q.message}`.toLowerCase().includes(needle)));
  }, [all, filter, employeeFilter, fieldFilter, search, names]);

  const total = matching.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = matching.slice(firstIndex, firstIndex + rowsPerPage);
  const filtersActive = employeeFilter !== '' || fieldFilter !== '';

  const goToPage = (n: number) => {
    const clamped = Math.min(Math.max(1, n), totalPages);
    setPage(clamped);
    setPageInput(String(clamped));
  };
  const pickTab = (next: Filter) => { setFilter(next); setReplyingId(null); goToPage(1); };

  const answered = (updated: EmployeeQuestionItem) => {
    setReplyingId(null);
    setAll((prev) => (prev ? prev.map((q) => (q.id === updated.id ? updated : q)) : prev));
  };

  const pills = [
    { key: 'employee', aria: 'Filter by employee', label: 'Employee Name', value: employeeFilter, onChange: setEmployeeFilter, options: employeeOptions },
    { key: 'field', aria: 'Filter by field', label: 'Field', value: fieldFilter, onChange: setFieldFilter, options: fieldOptions },
  ];

  return (
    <section className="card card--wide">
      <div className="tabs" role="tablist" aria-label="Question status">
        <button type="button" role="tab" aria-selected={filter === 'open'} className={filter === 'open' ? 'tab tab--active' : 'tab'} onClick={() => pickTab('open')}>
          Open{openCount === null ? '' : ` (${openCount})`}
        </button>
        <button type="button" role="tab" aria-selected={filter === 'answered'} className={filter === 'answered' ? 'tab tab--active' : 'tab'} onClick={() => pickTab('answered')}>
          Answered{answeredCount === null ? '' : ` (${answeredCount})`}
        </button>
      </div>
      <div className="employee-list">
        <div className="employee-list__header">
          <h2>Employee Questions</h2>
          <div className="employee-list__tools">
            <span className="employee-list__search-wrap">
              <SearchIcon />
              <input
                type="search" className="employee-list__search" placeholder="Search" aria-label="Search employee questions"
                value={search} onChange={(e) => { setSearch(e.target.value); goToPage(1); }}
              />
            </span>
          </div>
        </div>

        <div className="employee-list__filters" role="group" aria-label="Filters">
          {pills.map((p) => (
            <span className="pill" key={p.key}>
              <select aria-label={p.aria} value={p.value} onChange={(e) => { p.onChange(e.target.value); goToPage(1); }}>
                <option value="">{p.label}</option>
                {p.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </span>
          ))}
          {filtersActive && <button type="button" className="link" onClick={() => { setEmployeeFilter(''); setFieldFilter(''); goToPage(1); }}>Clear filters</button>}
        </div>

        <div className="employee-list__bar">
          <label className="employee-list__rows">
            Rows per page:{' '}
            <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); goToPage(1); }}>
              {ROWS_PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          {total > 0 && <span className="muted" aria-live="polite">{firstIndex + 1}-{firstIndex + pageRows.length} of {total} items</span>}
          {total > 0 && (
            <nav className="employee-list__paging" aria-label="Pagination">
              <button type="button" disabled={currentPage <= 1} onClick={() => goToPage(1)}>First</button>
              <button type="button" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>Previous</button>
              <input
                type="number" min={1} max={totalPages} aria-label="Page number" value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={() => {
                  const n = Number(pageInput);
                  if (Number.isFinite(n) && n >= 1) goToPage(Math.floor(n)); else setPageInput(String(currentPage));
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
              <span>of {totalPages}</span>
              <button type="button" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
              <button type="button" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>Last</button>
            </nav>
          )}
        </div>

        {error && <RetryableError message={error} onRetry={() => void load()} />}
        {!error && all === null && <p className="muted" role="status">Loading…</p>}
        {!error && all !== null && total === 0 && (
          <div className="employee-list__empty">
            <p role="status">
              {search.trim() || filtersActive ? 'No questions match your search or filters.' : filter === 'open' ? 'No open questions.' : 'No answered questions yet.'}
            </p>
          </div>
        )}

        {!error && total > 0 && (
          <div className="employee-list__table-wrap">
            <table className="employee-list__table">
              <thead>
                <tr>
                  <th scope="col">Employee Name</th>
                  <th scope="col">Field</th>
                  <th scope="col">Question</th>
                  <th scope="col">Asked</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((q) => (
                  <Fragment key={q.id}>
                    <tr>
                      <td>{nameOf(q)}</td>
                      <td>{questionFieldLabel(q.fieldKey)}</td>
                      <td className="question-text">{q.message}</td>
                      <td>{dateText(q.createdAtUtc)}</td>
                      <td>
                        <span className={`status-badge status-badge--${q.status === 'open' ? 'pending-invite' : 'active'}`}>
                          <span className="status-badge__dot" aria-hidden="true" />
                          {q.status === 'open' ? 'Open' : 'Answered'}
                        </span>
                      </td>
                      <td>
                        {q.status === 'open' ? (
                          <span className="employee-list__actions">
                            <button type="button" className="row-action row-action--resend" aria-label="Reply" title="Reply" aria-expanded={replyingId === q.id}
                              onClick={() => setReplyingId(replyingId === q.id ? null : q.id)}>
                              <ReplyIcon />
                              <span className="row-action__label" aria-hidden="true">Reply</span>
                            </button>
                          </span>
                        ) : <span className="muted">—</span>}
                      </td>
                    </tr>
                    {q.status === 'answered' && q.reply && (
                      <tr className="question-reply-row">
                        <td colSpan={6}><p className="question-text">Your reply{q.repliedAtUtc ? ` (${dateText(q.repliedAtUtc)})` : ''}: {q.reply}</p></td>
                      </tr>
                    )}
                    {replyingId === q.id && (
                      <tr className="question-reply-row">
                        <td colSpan={6}>
                          <QuestionFieldEditor employeeId={q.employeeId} fieldKey={q.fieldKey} />
                          <ReplyBox question={q} onAnswered={answered} onCancel={() => setReplyingId(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function ReplyBox({ question, onAnswered, onCancel }: { question: EmployeeQuestionItem; onAnswered: (q: EmployeeQuestionItem) => void; onCancel: () => void }) {
  const uid = useId();
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send() {
    const text = reply.trim();
    if (!text) { setError('Write a reply first.'); return; }
    setBusy(true);
    setError(null);
    try {
      onAnswered(await peopleGateway.replyToEmployeeQuestion(question.id, text));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not send the reply. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="question-reply">
      <label htmlFor={`${uid}-reply`}>Your reply</label>
      <p className="muted">The employee gets this reply by email and sees it in their setup. Do not include SSNs, bank details or passwords.</p>
      <textarea id={`${uid}-reply`} rows={3} maxLength={QUESTION_MAX_LENGTH} value={reply} disabled={busy} autoFocus
        aria-invalid={error ? true : undefined} onChange={(e) => { setReply(e.target.value); setError(null); }} />
      {error && <p className="error" role="alert">{error}</p>}
      <div className="question-reply__actions">
        <button type="button" className="employee-list__add" onClick={() => void send()} disabled={busy}>{busy ? 'Sending…' : 'Send reply'}</button>
        <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}
